import { memo, useCallback } from 'react';
import { FullWidth } from './styled';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import ReportSubSection from '@components/ReportSubSection';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useTracking } from 'react-tracking';
import { Button, Grid, Box, Row } from '@simplywallst/ui-core';
import { RouterLink } from '@redux-first-router-link';
import useInvestingIdea from '@/pages/CompanyReport/hooks/useInvestingIdea';
import type { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { getRedirectTextBySection, getIdeanameBySection } from './utils';
import type { CompanyReportSection } from '@/pages/CompanyReport/useSection';
import Tile, { Company } from '../Components/Tile';
import useDividendInvestingIdea from '@/pages/CompanyReport/hooks/useDividendInvestingIdea';
import TileSkeleton from '@/pages/Stocks/components/StockTiles/TileSkeleton';

const MemoTile = memo(Tile);

interface PureSimilarCompaniesProps {
  title: string;
  section: CompanyReportSection;
  market: PrimaryCountryISOElseGlobal;
  industry: string;
  companies: Company[];
  gridViewOptionId: GridViewOptions;
  isLoading?: boolean;
  isErrored?: boolean;
  companyId: string;
}

function PureSimilarCompanies({
  section,
  title,
  companies,
  market,
  industry,
  gridViewOptionId,
  isLoading,
  isErrored,
  companyId,
}: PureSimilarCompaniesProps) {
  const { isPristine } = useVisibleSectionsContext();

  const redirectText = getRedirectTextBySection({
    section,
    industry,
    market,
  });

  const ideaName = getIdeanameBySection(section);

  const generalPayload = useInvestingIdea(
    ideaName,
    gridViewOptionId,
    market,
    industry
  );
  const dividendPayload = useDividendInvestingIdea(market.toLowerCase());

  const payload = section === 'dividend' ? dividendPayload : generalPayload;

  const { Track, trackEvent } = useTracking({
    type: 'track',
  });

  const isSuccess = !isLoading && !isErrored;

  const trackNavigateToScreener = useCallback(
    (trigger: string) => {
      trackEvent({
        action: 'click',
        subject: `${section}_next_steps${trigger}`,
        modifier: 'grid',
        companyId,
      });
    },
    [companyId, section, trackEvent]
  );

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={title}
        body={
          <>
            <Box marginBottom="x2">
              <FullWidth>
                {isLoading && (
                  <Row>
                    <TileSkeleton />
                    <TileSkeleton />
                    <TileSkeleton />
                    <TileSkeleton />
                  </Row>
                )}
                {isSuccess && (
                  <Grid
                    gap="8px"
                    templateColumns="1fr"
                    templateColumnsSm="1fr 1fr"
                    templateColumnsMd="repeat(4, 1fr)"
                    justifyContent="flex-start"
                    $width="100%"
                  >
                    {companies.map((company, index) => {
                      return (
                        <MemoTile
                          key={`${company.companyId}-stock-tile`}
                          index={index}
                          company={company}
                          hideIndustryCol
                          section={section}
                        />
                      );
                    })}
                  </Grid>
                )}
              </FullWidth>
            </Box>
            {payload && payload.linkToInvestingIdea && (
              <RouterLink
                to={payload.linkToInvestingIdea}
                onClick={() => trackNavigateToScreener('')}
                noUnderline
              >
                <Button styleType="loudLarge" display="block">
                  {redirectText}
                </Button>
              </RouterLink>
            )}
          </>
        }
      />
    </Track>
  );
}

export default PureSimilarCompanies;
